import React, { useEffect, useState } from "react";
import { Box, Flex } from "@chakra-ui/react";
import { Plus, Eye, Pencil, Archive, EllipsisVertical } from "lucide-react";

import { useDispatch, useSelector } from "react-redux";
import {
  fetchLoyaltyConfigAction,
  createLoyaltyConfigAction,
  updateLoyaltyConfigAction,
  deleteLoyaltyConfigAction,
} from "../../../../state/actions/loyaltyConfigActions";

import ReusableTable from "components/Tables/ReusableTable";
import GradientButton from "components/Buttons/GradientButton";
import ThreeDotMenu from "components/Menus/ThreeDotMenu";

import AddDrawer from "./components/AddLoyaltyConfigDrawer";
import ViewDrawer from "./components/ViewLoyaltyConfigDrawer";
import EditDrawer from "./components/EditLoyaltyConfigDrawer";
import DeleteModal from "./components/DeleteLoyaltyConfigDrawer";

import { useCustomNotification } from "components/Toasts/OpenNotification";

const LoyaltyConfigPage = () => {
  const dispatch = useDispatch();
  const notify = useCustomNotification();

  const { config } = useSelector((state) => state.loyaltyConfig);

  const [selected, setSelected] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchLoyaltyConfigAction());
  }, [dispatch]);

  const tableData = config ? [config] : [];



  const handleAdd = async (data) => {
    try {
      await dispatch(createLoyaltyConfigAction(data));
      setAddOpen(false);

      notify({
        type: "success",
        message: "Config Created",
        description: "Loyalty configuration created successfully.",
      });
    } catch (err) {
      notify({
        type: "error",
        message: "Error",
        description: "Failed to create configuration.",
      });
    }
  };

  const handleUpdate = async (data) => {
    try {
      await dispatch(updateLoyaltyConfigAction(data));
      setEditOpen(false);

      notify({
        type: "success",
        message: "Config Updated",
        description: "Loyalty configuration updated successfully.",
      });
    } catch (err) {
      notify({
        type: "error",
        message: "Error",
        description: "Failed to update configuration.",
      });
    }
  };

  const handleDelete = async () => {
    try {
      await dispatch(deleteLoyaltyConfigAction());
      setDeleteOpen(false);

      notify({
        type: "success",
        message: "Config Deleted",
        description: "Loyalty configuration deleted successfully.",
      });
    } catch (err) {
      notify({
        type: "error",
        message: "Error",
        description: "Failed to delete configuration.",
      });
    }
  };


  const columns = [
    { Header: "Points/Pula", accessor: "points_per_pula" },
    { Header: "Referral %", accessor: "referral_percentage" },
    { Header: "Min Points", accessor: "min_points" },
    { Header: "Max Points", accessor: "max_points" },
    {
      Header: "Actions",
      Cell: ({ row }) => (
        <ThreeDotMenu
          icon={<EllipsisVertical size={16} />}
          items={[
            {
              text: "View",
              icon: <Eye size={16} />,
              onClick: () => {
                setSelected(row.original);
                setViewOpen(true);
              },
            },
            {
              text: "Edit",
              icon: <Pencil size={16} />,
              onClick: () => {
                setSelected(row.original);
                setEditOpen(true);
              },
            },
            {
              text: "Delete",
              icon: <Archive size={16} />,
              onClick: () => {
                setSelected(row.original);
                setDeleteOpen(true);
              },
            },
          ]}
        />
      ),
    },
  ];

  return (
    <Box>
      <Flex justify="flex-end" mb={4}>
        <GradientButton
          icon={<Plus size={16} />}
          onClick={() => setAddOpen(true)}
        >
          Add Config
        </GradientButton>
      </Flex>

      <ReusableTable columnsData={columns} tableData={tableData} />

      {/* DRAWERS */}
      <AddDrawer
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onAdd={handleAdd}
      />

      <ViewDrawer
        isOpen={viewOpen}
        onClose={() => setViewOpen(false)}
        config={selected}
      />

      <EditDrawer
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        config={selected}
        onUpdate={handleUpdate}
      />

      <DeleteModal
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
      />
    </Box>
  );
};

export default LoyaltyConfigPage;